import type { RequestInit } from '@vercel/fetch'
import { logFetchQueryAsCurl, logGqlErrors } from './logGraphqlFetchErrors'
import { cache } from 'swr'
import { createCart } from './api/operations/create-cart'
import * as Sentry from '@sentry/nextjs'

const defaultLocale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'en'

export interface GraphQLError {
    message: string
    extensions: {
        category: string
    }
    locations: Array<{
        line: number
        column: number
    }>
    debugMessage: string
}

// This will remove all space from queries except absolute minimum spaces
const getShortQueryString = (query: string): string => {
    return query
        .replace(/#.*\n/g, '')
        .replace(/[\s|,]*\n+[\s|,]*/g, ' ')
        .replace(/:\s/g, ':')
        .replace(/,\s/g, ',')
        .replace(/\)\s\{/g, '){')
        .replace(/\}\s/g, '}')
        .replace(/\{\s/g, '{')
        .replace(/\s\}/g, '}')
        .replace(/\s\{/g, '{')
        .replace(/\)\s/g, ')')
        .replace(/\(\s/g, '(')
        .replace(/\s\)/g, ')')
        .replace(/\s\(/g, '(')
        .replace(/=\s/g, '=')
        .replace(/\s=/g, '=')
        .replace(/@\s/g, '@')
        .replace(/\s@/g, '@')
        .replace(/\s\$/g, '$')
        .replace(/\s\./g, '.')
        .trim()
}

enum QueryType {
    query = 'query',
    mutation = 'mutation',
    subscription = 'subscription'
}

type GetQueryParamsResult = {
    operationType: QueryType
    operationNickname: string
    firstQueryName: string
}

/**
 * Extracts query parameters from shortened graphql query
 * @param query short query string (processed with `getShortQueryString`)
 * @returns query parameters necessary for custom headers and request body
 */
const getQueryParams = (query: string): GetQueryParamsResult => {
    const operationMethods = Object.keys(operationToMethod).join('|')
    const queryParamExtractionRegExp = new RegExp(
        `^(?:(${operationMethods}) ?)?(?:(\\w+)(?:\\(.+?\\))?)?\\{(?:\\w+:)?(\\w+)`
    )

    const params = queryParamExtractionRegExp.exec(query) || []

    if (!params) {
        const errorMessage = `Unable to get query parameters, i.e. 'type', 'operationNickname', 'firstQueryName', from the following query: "${query}"`

        console.error(errorMessage)
        throw new Error(errorMessage)
    }

    const firstQueryName = params[3]
    const [, operationType = QueryType.query, operationNickname] = params

    return {
        operationType: operationType as QueryType,
        operationNickname,
        firstQueryName
    }
}

const operationToMethod = {
    [QueryType.query]: 'GET',
    [QueryType.mutation]: 'POST',
    [QueryType.subscription]: 'POST'
}

export type GraphqlFetchOutput<OutputInterface> = {
    data: OutputInterface
    errors: GraphQLError[]
}

export type GraphqlFetchOutputPromise<OutputInterface> = Promise<
    GraphqlFetchOutput<OutputInterface>
>

export type GraphqlFetchParameters<VariablesInterface> = {
    query: string
    variables?: VariablesInterface
    locale?: string
    fetchOptions?: RequestInit
    isAuthorised?: boolean
}

export const graphqlFetch = async <VariablesInterface, OutputInterface>({
    query,
    variables,
    locale,
    fetchOptions,
    isAuthorised
}: GraphqlFetchParameters<VariablesInterface>): GraphqlFetchOutputPromise<OutputInterface> => {
    const shortQuery = getShortQueryString(query)
    const { operationType, operationNickname, firstQueryName } = getQueryParams(
        shortQuery
    )

    const operationNicknameFormatted = operationNickname
        ? `&operationName=${operationNickname}`
        : ''

    // get the URL ending for queries with parameters
    // leave it empty for mutations
    const urlEnding =
        operationType === QueryType.query
            ? `?query=${encodeURIComponent(
                  shortQuery
              )}${operationNicknameFormatted}&variables=${encodeURIComponent(
                  JSON.stringify(variables || {})
              )}`
            : ''
    const headers = { ...fetchOptions?.headers } as Record<string, any>
    const baseUrl = process.env.NEXT_PUBLIC_API_GATEWAY_URL

    if (operationToMethod[operationType] === 'POST') {
        headers['Content-Type'] = 'application/json'
    }

    const baseStoreCode = process.env.NEXT_PUBLIC_STORE_CODE
    const defaultLocaleOverride =
        process.env.NEXT_PUBLIC_DEFAULT_LOCALE_OVERRIDE || null
    if (defaultLocaleOverride) {
        if (
            baseStoreCode &&
            locale &&
            locale !== 'default' &&
            locale !== defaultLocaleOverride
        ) {
            headers.Store = `${baseStoreCode}_${locale}`
        } else if (baseStoreCode) {
            headers.Store = `${baseStoreCode}_${defaultLocaleOverride}`
        }
    } else {
        if (baseStoreCode && locale && locale !== defaultLocale) {
            headers.Store = `${baseStoreCode}_${locale}`
        } else if (baseStoreCode) {
            headers.Store = baseStoreCode
        } else if (locale && locale !== defaultLocale) {
            headers.Store = locale
        }
    }

    if (typeof window !== 'undefined') {
        const savedToken = window.localStorage.getItem('pylot_token') || null
        if (savedToken) {
            headers.Authorization = `Bearer ${savedToken}`
        }
    }

    // init the fetch config early so we can manipulate below
    const fetchConfig = {
        ...fetchOptions,
        method: operationToMethod[operationType],
        headers
    }

    // Add body ONLY IF not a query
    // Adding body to a query breaks the query
    if (operationType !== QueryType.query)
        fetchConfig.body = JSON.stringify({
            query,
            variables
        })

    const fetchUrl = baseUrl + urlEnding
    let res
    try {
        res = await fetch(
            fetchUrl,
            // @ts-ignore for some reason, typescript insists that we need `body`,
            // which is only appropriate for POST requests
            fetchConfig
        )
    } catch (e) {
        // Log service connection errors
        logFetchQueryAsCurl(fetchUrl, fetchConfig)
        // Capture connection errors in Sentry
        Sentry.captureException(e, {
            tags: {
                errorType: 'graphql_connection_error',
                operationType,
                operationName: operationNickname || firstQueryName
            },
            contexts: {
                graphql: {
                    url: fetchUrl,
                    method: operationToMethod[operationType],
                    operationType,
                    operationName: operationNickname || firstQueryName
                }
            },
            extra: {
                fetchConfig: {
                    method: fetchConfig.method,
                    headers: Object.keys(fetchConfig.headers || {})
                }
            }
        })
        throw new Error(`Failed to connect to the service: ${e.message}`)
    }
    // We don't return 'await res.json()' right away in order to be able to log erroneous response text and headers
    const resText = await res.text()
    try {
        const resJson = JSON.parse(resText)
        if (resJson && (resJson['errors'] || resJson['error'])) {
            logGqlErrors(fetchUrl, fetchConfig, res, resJson)
            const errors = resJson['errors'] || [resJson['error']]
            errors.forEach((error: GraphQLError) => {
                Sentry.captureException(
                    new Error(error.message || 'GraphQL Error'),
                    {
                        tags: {
                            errorType: 'graphql_error',
                            operationType,
                            operationName: operationNickname || firstQueryName,
                            errorCategory:
                                error.extensions?.category || 'unknown'
                        },
                        contexts: {
                            graphql: {
                                url: fetchUrl,
                                method: operationToMethod[operationType],
                                operationType,
                                operationName:
                                    operationNickname || firstQueryName,
                                error: {
                                    message: error.message,
                                    extensions: error.extensions,
                                    locations: error.locations,
                                    debugMessage: error.debugMessage
                                }
                            }
                        },
                        level:
                            error.extensions?.category ===
                            'graphql-authorization'
                                ? 'warning'
                                : 'error'
                    }
                )
            })
            if (
                !isAuthorised &&
                typeof window !== 'undefined' &&
                // check if any of the errors are 'graphql-authorization' errors
                resJson.errors.filter(
                    (error: { extensions?: { category: string } }) =>
                        error.extensions?.category ===
                            'graphql-authorization' ||
                        error.extensions?.category === 'graphql-authentication'
                ).length >= 1 &&
                window.localStorage.getItem('pylot_token') &&
                (window.localStorage.getItem('pylot_token') as string).length >
                    0 &&
                // do not expire a session that was just created (5s grace period after login)
                (() => {
                    const t = window.localStorage.getItem('pylot_login_time')
                    return !t || Date.now() - parseInt(t) > 5000
                })()
            ) {
                window.location.hash = 'session-expired'
                localStorage.removeItem('pylot_token')
                localStorage.removeItem('public_hash')
                localStorage.removeItem('cart_id')
                cache.clear()
                window.localStorage.setItem('is_authorised', '1')
            }
            if (
                typeof window !== 'undefined' &&
                resJson.errors.filter(
                    (error: {
                        extensions?: { category: string }
                        message?: string
                        path: [string]
                    }) =>
                        error?.path?.[0]?.includes('art') &&
                        (error.message?.includes(
                            'Could not find a cart with ID'
                        ) ||
                            error.message?.includes(
                                'Required parameter "cart_id" is missing'
                            ) ||
                            error.message?.includes("The cart isn't active."))
                ).length >= 1
            ) {
                //create cart when Could not find a cart with ID is thrown.
                createCart({ locale })
            }
        }
        return resJson
    } catch (e) {
        // Show the actual response text and error message
        console.error(e)
        logFetchQueryAsCurl(fetchUrl, fetchConfig)
        // Capture JSON parsing errors in Sentry
        Sentry.captureException(e, {
            tags: {
                errorType: 'graphql_json_parse_error',
                operationType,
                operationName: operationNickname || firstQueryName
            },
            contexts: {
                graphql: {
                    url: fetchUrl,
                    method: operationToMethod[operationType],
                    operationType,
                    operationName: operationNickname || firstQueryName,
                    responseStatus: res.status,
                    responseText: resText.substring(0, 1000) // Limit text length
                }
            }
        })
        throw new Error(`Failed to process response as JSON data: ${resText}`)
    }
}
